import React, { Component } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import 'react-image-lightbox/style.css';

import './fonts/DINNextRoundedLTPro.css';
import './fonts/icomoon.css';
import './App.scss';
import './utils/vh-trick';
import { responseStatus } from './utils/fetch';
import scenes from './components/Scenes';
import Scene from './components/Scene';
import ScrollIndicator from './components/ScrollIndicator';

/**
 * App
 */

/**
 * The global App parameters
 *
 * @typedef  {Object} Params
 * @property {number} slideLength - The scroll length of one slide
 * @property {number} slideEventsThresold - The thresold to apply to slide events dispatching, in percent of the slide width
 * @property {number} slideScrollingDuration - The GSAP ScrollToPlugin scroll duration per slide, in seconds
 * @property {number} scrollingMaxDuration - The maximum GSAP ScrollToPlugin scroll duration, in seconds
 * @property {number} scrollVelocityThresold - The velocity thresold used to start things only if scroll speed isn't too high
 * @property {Object} timelineDefaults - The GSAP Timeline default settings
 * @property {Object} scrollTriggerDefaults - The GSAP ScrollTrigger default settings
 * @property {string} scrollTriggerId - The id of the main GSAP ScrollTrigger
 * @property {number[]} [scenesLengths] - The scroll length of each scene
 * @property {number[]} [scenesAnchors] - The scroll anchor of each scene
 * @property {number[][]} [slidesAnchors] - The scroll anchor of each slide, for each scene
 * @property {number} [timelineLength] - The scroll length of the timeline
 * @property {number} [timelineEnd] - The scroll end of the timeline
 */

/**
 * The dispatcher records the index of the last fired events so they can be fired only once
 *
 * @typedef  {Object} DispatcherRecords
 * @property {Object} scrollUp - The scroll direction
 * @property {number} scrollUp.onStart - The last onStart index fired
 * @property {number} scrollUp.onEnd - The last onEnd index fired
 */

/**
 * The React State
 *
 * @typedef  {Object} State
 * @property {bool} isLoaded - Records if the data file is loaded
 * @property {number} activeScene - The active scene index
 * @property {number} activeSlide - The active slide index in the current scene
 * @property {bool} isScrolling - Records if ScrollToPlugin is scrolling
 * @property {Object} scrollIndicator - The ScrollIndicator properties
 * @property {number} scrollIndicator.duration - The duration of the scroll tween
 * @property {number} scrollIndicator.direction (1:down)|(-1:up) - The direction of the scroll tween
 */

class App extends Component {
  constructor(props) {
    super(props);

    /** Register GSAP Plugins */
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

    /**
     * @type   {Params}
     */
    this.params = {
      slideLength:
        document.documentElement.clientWidth > document.documentElement.clientHeight
          ? document.documentElement.clientWidth
          : document.documentElement.clientHeight,
      slideEventsThresold: 0.5,
      slideScrollingDuration: 0.125,
      scrollingMaxDuration: 2.25,
      scrollVelocityThresold: 1.25,
      timelineDefaults: {
        ease: 'none',
        duration: 2,
      },
      scrollTriggerDefaults: {
        invalidateOnRefresh: true,
        scrub: 0.75,
        start: 'top top',
      },
      scrollTriggerId: 'main',
    };

    /**
     * @type   {DispatcherRecords}
     */
    this.dispatcherRecords = {
      scrollUp: {
        onStart: -1,
        onEnd: -1,
      },
    };

    /**
     * @type   {State}
     */
    this.state = {
      isLoaded: false,
      activeScene: 0,
      activeSlide: 0,
      isScrolling: false,
      scrollIndicator: {
        duration: this.params.slideScrollingDuration * 2,
        direction: 1,
      },
    };

    /**
     * React Refs
     */
    this.scenesWrapper = React.createRef();
    this.scenes = scenes.map(() => React.createRef());
    this.scrollIndicator = React.createRef();
  }

  /**
   * Invoked after the component is mounted
   */
  componentDidMount() {
    /** Fetch the content from the json file and initialize the App */
    fetch(`./${process.env.REACT_APP_DATA_FILE}`)
      .then(responseStatus)
      .then((response) => response.json())
      .then((json) => {
        this.data = json;
        this.computeParams(this.params);
        this.setState({ isLoaded: true });
        this.mountTimeline();
        this.removeAppLoader();
      })
      .catch((error) => console.warn(error));
  }

  /**
   * Remove the App CSS loader
   */
  removeAppLoader() {
    const appRoot = document.getElementById('root');
    appRoot.classList.add('is-loaded');
    /** Timeout must match the CSS transition time of the loader */
    setTimeout(() => {
      appRoot.classList.remove('is-loading');
      appRoot.classList.remove('is-loaded');
    }, 750);
  }

  /**
   * Compute the App parameters
   *
   * @param  {Params} params - The default parameters
   */
  computeParams(params) {
    params.scenesLengths = [];
    params.scenesAnchors = [];
    params.slidesAnchors = [];
    this.data.forEach((scene, index) => {
      params.scenesLengths[index] = index === 0 ? params.slideLength : scene.slides.length * params.slideLength;
      params.scenesAnchors[index] = index === 0 ? 0 : params.scenesAnchors[index - 1] + params.scenesLengths[index - 1];
      params.slidesAnchors[index] = [];
      scene.slides.forEach((slide, slideIndex) => {
        params.slidesAnchors[index].push(params.scenesAnchors[index] + slideIndex * params.slideLength);
      });
    });
    params.timelineLength = params.scenesLengths.reduce((a, b) => a + b, 0);
    params.timelineEnd = params.timelineLength - params.slideLength;
  }

  /**
   * Mount the App timeline
   */
  mountTimeline() {
    const timeline = gsap.timeline({
      defaults: this.params.timelineDefaults,
      scrollTrigger: {
        ...this.params.scrollTriggerDefaults,
        id: this.params.scrollTriggerId,
        pin: true,
        snap: {
          snapTo: 'labels', // Can be labelsDirectional
          delay: 0.1,
          duration: { min: 0, max: 0.75 },
        },
        trigger: this.scenesWrapper.current,
        end: `=${this.params.timelineEnd}`,
        onSnapComplete: (trigger) => {
          /** Kill the scrub and reset the position of the timeline */
          this.resetPositions(trigger);

          /** Update the active indexes */
          this.setActiveIndexes(trigger);
        },
      },
    });

    /** Set up ScrollTrigger media queries */
    ScrollTrigger.matchMedia({
      '(orientation: landscape)': () => this.mountScenes(timeline, 'landscape'),
      '(orientation: portrait)': () => this.mountScenes(timeline, 'portrait'),
    });
    // this.console('mountTimeline params', this.params);
  }

  /**
   * Set the activeScene and activeSlide state indexes regarding ScrollTrigger position
   *
   * @param {gsap.plugins.ScrollTrigger} trigger
   */
  setActiveIndexes(trigger) {
    const position = trigger.progress * this.params.timelineLength;
    const scenesAnchors = [...this.params.scenesAnchors, this.params.timelineEnd];
    let activeScene = 0;
    let activeSlide = 0;
    this.params.scenesAnchors.forEach((anchor, index) => {
      if (position >= scenesAnchors[index] && position < scenesAnchors[index + 1]) {
        activeScene = index;
        const slidesAnchors = [...this.params.slidesAnchors[index], scenesAnchors[index + 1]];
        slidesAnchors.forEach((anchor, slideIndex) => {
          if (position >= slidesAnchors[slideIndex] && position < slidesAnchors[slideIndex + 1]) {
            activeSlide = slideIndex;
          }
        });
      }
    });
    this.setState({ activeScene, activeSlide });
    // this.console(`setActiveIndexes S${activeScene}.${activeSlide}`);
  }

  /**
   * Mount the scenes to the timeline regarding screen orientation
   *
   * @param  {gsap.core.Timeline} timeline - The main Timeline object
   * @param  {string} orientation landscape|portrait - The screen orientation
   * @return {{gsap.core.Timeline.clear()}} - A function that clears the Timeline on media query break
   */
  mountScenes(timeline, orientation) {
    timeline.addLabel('start');
    this.scenes.forEach((scene, index) => {
      if (index > 0) {
        /** The Scene events listeners */
        const listeners = {
          onStartParams: [index, scene],
          onStart: (index, scene) => this.dispatchSceneEvents(index, scene),
          onCompleteParams: [index, this.scenes[index - 1]],
          onComplete: (index, previousScene) => this.dispatchSceneEvents(index, undefined, previousScene),
          onUpdateParams: [index, scene, this.scenes[index - 1], timeline],
          onUpdate: (index, scene, previousScene, timeline) => this.dispatchSceneEvents(index, scene, previousScene, timeline),
        };

        /** The main scenes */
        if (orientation === 'landscape') {
          timeline.to(scene.current.element.current, {
            ...listeners,
            x: () => -(scene.current.element.current.offsetWidth - scene.current.tab.current.element.current.offsetWidth),
          });
        } else if (orientation === 'portrait') {
          timeline.to(scene.current.element.current, {
            ...listeners,
            y: () => -(scene.current.element.current.offsetHeight - scene.current.tab.current.element.current.offsetHeight),
          });
        }
        timeline.addLabel(`scene-${scene.current.props.index}`);

        /** The timeline of each Scene */
        const currentPosition = timeline.labels[`scene-${scene.current.props.index}`];
        if (scene.current.timeline !== undefined) {
          timeline.add(scene.current.timeline);
          Object.entries(scene.current.timeline.labels).forEach((label) => timeline.addLabel(label[0], currentPosition + label[1]));
        }
      }
    });

    /** Add an appear effect to the logo on the header tab */
    timeline.to(this.scenes[0].current.tab.current.logo.current, { opacity: 1 }, 0);
    // this.console('mountScenes timeline', timeline);

    /** Clear the timeline on media query break */
    return () => timeline.clear();
  }

  /**
   * Dispatch the Scene events
   *
   * @param  {number} index - The index of the scene
   * @param  {React.Component} scene - The scene component
   * @param  {React.Component} previousScene  - The previous scene component
   * @param  {gsap.core.Timeline} timeline - The scene Timeline
   *
   * @fires  Scene#onEnter - Multiple times when the scene is entering the screen
   * @fires  Scene#onLeave - Multiple times when the scene is leaving the screen
   * @fires  Scene#onStart - Once when the scene timeline starts
   * @fires  Scene#onEnd - Once when the scene timeline ends
   */
  dispatchSceneEvents(index, scene, previousScene, timeline) {
    if (!this.state.isScrolling) {
      if (scene === undefined && previousScene === undefined && timeline === undefined) {
        if (this.dispatcherRecords.scrollUp.onEnd !== index && index < this.scenes.length - 1) {
          /**
           * @fires  Scene#onEnd - To the next scene, scrolling up (called from Scene#timeline#onUpdate)
           */
          this.scenes[index + 1].current.onEnd(-1);
          this.dispatcherRecords.scrollUp.onEnd = index;
        }
      } else if (previousScene === undefined && timeline === undefined) {
        /**
         * @fires  Scene#onStart - To the current scene, scrolling down (called from this#mountScenes#listeners#onStart)
         */
        scene.current.onStart(1);
        this.dispatcherRecords.scrollUp.onEnd = -1;
      } else if (scene === undefined && timeline === undefined) {
        /**
         * @fires  Scene#onEnd - To the previous scene, scrolling down (called from this#mountScenes#listeners#onComplete)
         */
        previousScene.current.onEnd(1);
        this.dispatcherRecords.scrollUp.onStart = -1;
      } else {
        /**
         * (called from this#mountScenes#listeners#onUpdate)
         */
        const progressLength = timeline.scrollTrigger.progress * this.params.timelineEnd;
        const scenesAnchors = [...this.params.scenesAnchors, this.params.timelineEnd];
        if (progressLength > 1 && progressLength > scenesAnchors[index] - this.params.slideLength && progressLength <= scenesAnchors[index]) {
          if (timeline.scrollTrigger.direction === 1) {
            /**
             * @fires  Scene#onEnter - To the current scene, scrolling down
             */
            scene.current.onEnter(timeline.scrollTrigger.direction, progressLength);
            /**
             * @fires  Scene#onLeave - To the previous scene, scrolling down
             */
            previousScene.current.onLeave(timeline.scrollTrigger.direction, progressLength);
          } else {
            /**
             * @fires  Scene#onEnter - To the previous scene, scrolling up
             */
            previousScene.current.onEnter(timeline.scrollTrigger.direction, progressLength);
            /**
             * @fires  Scene#onLeave - To the the current scene, scrolling up
             */
            scene.current.onLeave(timeline.scrollTrigger.direction, progressLength);

            if (progressLength <= scenesAnchors[index]) {
              if (this.dispatcherRecords.scrollUp.onStart !== index) {
                /**
                 * @fires  Scene#onStart - To the previous scene, scrolling up
                 */
                previousScene.current.onStart(-1);
                this.dispatcherRecords.scrollUp.onStart = index;
              }
            }
          }
        }
      }
    }
  }

  /**
   * Change the active scene to the given index using ScrollToPlugin
   *
   * @param  {MouseEvent} event - The mouse event
   * @param  {number} index - The index of the scene to navigate to
   */
  changeScene(event, index) {
    event.preventDefault();
    event.currentTarget.blur();

    /** Calculate the scroll direction and duration */
    const sceneGap = this.params.scenesAnchors[index] - this.params.slidesAnchors[this.state.activeScene][this.state.activeSlide];
    const direction = sceneGap > 0 ? 1 : -1;
    let duration = Math.abs(sceneGap) / (this.params.slideLength / this.params.slideScrollingDuration);
    duration = duration < this.params.scrollingMaxDuration ? duration : this.params.scrollingMaxDuration;
    duration = duration > 0 ? duration : this.params.slideScrollingDuration;

    /** Stop the animation and medias of the current scene */
    this.scenes[this.state.activeScene].current.stopAnimation();
    this.scenes[this.state.activeScene].current.stopSlideMedias(this.state.activeSlide);

    /** Update the state */
    this.setState({ activeScene: index, activeSlide: 0, isScrolling: true, scrollIndicator: { duration, direction } });
    // this.console(`changeScene S${index}`, 'sceneGap', sceneGap, 'duration', duration);

    /** Scroll the window to the scene */
    gsap.to(window, {
      duration,
      scrollTo: this.params.scenesAnchors[index],
      onComplete: () => {
        /** Start the scene animation and unmount the ScrollIndicator */
        this.scenes[index].current.startAnimation();
        this.scrollIndicator.current.unmount();
      },
    });
  }

  /**
   * Invoked when changeScene has completed scrolling, after ScrollIndicator unmount
   */
  onSceneChangeComplete() {
    /** Start the first slide tweens */
    this.scenes[this.state.activeScene].current.startSlideTweens(0, true);

    /** Kill the scrub and reset the position of the timeline */
    this.resetPositions(ScrollTrigger.getById(this.params.scrollTriggerId));

    /** Update the state */
    this.setState({ isScrolling: false });
    // this.console(`onSceneChangeComplete S${this.state.activeScene}`);
  }

  /**
   * Kill the scrub and reset the position of the animation
   *
   * @param {gsap.ScrollTrigger.trigger} trigger
   */
  resetPositions(trigger) {
    const scrubDuration = !isNaN(this.params.scrollTriggerDefaults.scrub) ? this.params.scrollTriggerDefaults.scrub * 1000 : 0;
    setTimeout(() => {
      const scrub = trigger.getTween ? trigger.getTween() : gsap.getTweensOf(trigger.animation)[0];
      scrub && scrub.kill();
      trigger.animation.progress(trigger.progress);
      // this.console('resetPositions scrub', scrub);
    }, scrubDuration);
  }

  /**
   * Log anything to the console, adding current time and active scene and slide state indexes
   *
   * @param  {...any} params
   */
  console(...params) {
    /** Only log in development environment */
    if (process.env.NODE_ENV === 'development') {
      const now = new Date();
      const time = `${now.getHours()}:${now.getMinutes()}:${now.getSeconds()}.${now.getMilliseconds()}`;
      let active = '';
      if (this.state) {
        active = `S${this.state.activeScene}.${this.state.activeSlide}`;
      }
      console.info(`${time} ${active}:`, ...params);
    }
  }

  render() {
    const DevTools = process.env.NODE_ENV === 'development' ? React.lazy(() => import('./Dev')) : <></>;
    return (
      <>
        {this.state.isLoaded && (
          <main ref={this.scenesWrapper} className="scenes-wrapper" style={{ '--scene-count': scenes.length }}>
            {scenes.map((scene, index) => (
              <Scene
                key={index}
                ref={this.scenes[index]}
                index={index}
                Scene={scene}
                data={this.data[index]}
                params={this.params}
                downloadLink={this.data[0].slides[0].link}
                onSceneChange={(event, index) => this.changeScene(event, index)}
                isScrolling={this.state.isScrolling}
                onScrollUp={(index) => this.dispatchSceneEvents(index)}
                console={(...params) => this.console(...params)}
              />
            ))}
            {this.state.isScrolling && (
              <ScrollIndicator
                ref={this.scrollIndicator}
                duration={this.state.scrollIndicator.duration}
                direction={this.state.scrollIndicator.direction}
                transitionDuration={this.params.slideScrollingDuration}
                onUnmount={() => this.onSceneChangeComplete()}
              />
            )}
          </main>
        )}
        {process.env.NODE_ENV === 'development' && (
          /** Development tools */
          <React.Suspense fallback={<></>}>
            <DevTools />
          </React.Suspense>
        )}
      </>
    );
  }
}

export default App;
